import React, { useState, useContext, useEffect } from 'react'
import { todayShort } from '../../utils/dates.js'
import * as api from '../../services/api.js'
import Card from '../../components/Card.jsx'
import { ThemeContext } from '../../context/ThemeContext.jsx'
import { useEmoji } from '../../hooks/useEmoji.jsx'

const STATES = [
  {
    pose: 'sad', name: 'Sad', icon: '~', color: 'var(--stone)',
    caption: 'Curled up, tail drooped, needing gentleness',
    desc: "It's okay to feel sad. You don't have to fix it today.",
  },
  {
    pose: 'anxious', name: 'Anxious', icon: '–', color: 'var(--stone)',
    caption: 'Wide eyes, trembling a little, on high alert',
    desc: 'Anxiety is just the cat worrying. It will pass.',
  },
  {
    pose: 'calm', name: 'Calm', icon: '◦', color: 'var(--sage)',
    caption: 'Sitting quietly, blinking slowly at the world',
    desc: "Calm is enough. You're here.",
  },
  {
    pose: 'happy', name: 'Happy', icon: '◉', color: 'var(--bark)',
    caption: 'Tail wagging, eyes bright, feeling good',
    desc: "Hold onto this — it's real.",
  },
  {
    pose: 'excited', name: 'Excited', icon: '★', color: 'var(--accent)',
    caption: 'Bouncing, ears up, tail a blur — full of life',
    desc: 'Something is sparkling in you today.',
  },
]

/* ── Mini cat SVGs — all colors use CSS variables via style prop ── */
function MiniCatSad() {
  return (
    <svg width="28" height="26" viewBox="0 0 60 55" style={{ display: 'block', margin: '0 auto' }}>
      <ellipse cx="30" cy="40" rx="22" ry="12" style={{ fill: 'var(--stone)' }}/>
      <ellipse cx="30" cy="26" rx="16" ry="14" style={{ fill: 'var(--stone)' }}/>
      <polygon points="18,16 12,3 22,12" style={{ fill: 'var(--stone)' }}/>
      <polygon points="42,16 48,3 38,12" style={{ fill: 'var(--stone)' }}/>
      <ellipse cx="30" cy="26" rx="10" ry="10" style={{ fill: 'var(--surf)' }}/>
      <ellipse cx="24" cy="24" rx="3" ry="2" style={{ fill: 'var(--ink)' }}/>
      <ellipse cx="36" cy="24" rx="3" ry="2" style={{ fill: 'var(--ink)' }}/>
      <path d="M25 33 Q30 30 35 33" fill="none" style={{ stroke: 'var(--sage)' }} strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  )
}
function MiniCatAnxious() {
  return (
    <svg width="28" height="26" viewBox="0 0 60 55" style={{ display: 'block', margin: '0 auto' }}>
      <ellipse cx="30" cy="40" rx="20" ry="11" style={{ fill: 'var(--stone)' }}/>
      <ellipse cx="30" cy="25" rx="16" ry="15" style={{ fill: 'var(--stone)' }}/>
      <polygon points="17,15 10,2 20,11" style={{ fill: 'var(--stone)' }}/>
      <polygon points="43,15 50,2 40,11" style={{ fill: 'var(--stone)' }}/>
      <ellipse cx="30" cy="25" rx="10" ry="10" style={{ fill: 'var(--surf)' }}/>
      <ellipse cx="24" cy="22" rx="4.5" ry="5" style={{ fill: 'var(--ink)' }}/>
      <ellipse cx="36" cy="22" rx="4.5" ry="5" style={{ fill: 'var(--ink)' }}/>
      <circle cx="25" cy="21" r="1.5" fill="white"/>
      <circle cx="37" cy="21" r="1.5" fill="white"/>
      <path d="M24 33 Q27 31 30 33 Q33 31 36 33" fill="none" style={{ stroke: 'var(--sage)' }} strokeWidth="1.1" strokeLinecap="round"/>
    </svg>
  )
}
function MiniCatCalm() {
  return (
    <svg width="28" height="26" viewBox="0 0 60 55" style={{ display: 'block', margin: '0 auto' }}>
      <ellipse cx="30" cy="40" rx="22" ry="12" style={{ fill: 'var(--stone)' }}/>
      <ellipse cx="30" cy="25" rx="17" ry="15" style={{ fill: 'var(--stone)' }}/>
      <polygon points="16,15 10,2 22,11" style={{ fill: 'var(--stone)' }}/>
      <polygon points="44,15 50,2 38,11" style={{ fill: 'var(--stone)' }}/>
      <ellipse cx="30" cy="25" rx="11" ry="11" style={{ fill: 'var(--surf)' }}/>
      <path d="M21 22 Q25 26 30 22 Q35 26 39 22" fill="none" style={{ stroke: 'var(--ink)' }} strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M25 33 Q30 36 35 33" fill="none" style={{ stroke: 'var(--sage)' }} strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  )
}
function MiniCatHappy() {
  return (
    <svg width="28" height="28" viewBox="0 0 60 58" style={{ display: 'block', margin: '0 auto' }}>
      <ellipse cx="30" cy="43" rx="22" ry="13" style={{ fill: 'var(--stone)' }}/>
      <ellipse cx="30" cy="26" rx="18" ry="16" style={{ fill: 'var(--stone)' }}/>
      <polygon points="16,14 8,0 20,10" style={{ fill: 'var(--stone)' }}/>
      <polygon points="44,14 52,0 40,10" style={{ fill: 'var(--stone)' }}/>
      <ellipse cx="30" cy="26" rx="12" ry="12" style={{ fill: 'var(--surf)' }}/>
      <ellipse cx="23" cy="23" rx="4.5" ry="5.5" style={{ fill: 'var(--ink)' }}/>
      <ellipse cx="37" cy="23" rx="4.5" ry="5.5" style={{ fill: 'var(--ink)' }}/>
      <circle cx="24.5" cy="21.5" r="1.8" fill="white"/>
      <circle cx="38.5" cy="21.5" r="1.8" fill="white"/>
      <path d="M23 36 Q30 41 37 36" fill="none" style={{ stroke: 'var(--sage)' }} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  )
}
function MiniCatExcited() {
  return (
    <svg width="28" height="30" viewBox="0 0 60 62" style={{ display: 'block', margin: '0 auto' }}>
      <polygon points="14,20 6,2 18,14" style={{ fill: 'var(--stone)' }}/>
      <polygon points="46,20 54,2 42,14" style={{ fill: 'var(--stone)' }}/>
      <ellipse cx="30" cy="46" rx="22" ry="13" style={{ fill: 'var(--stone)' }}/>
      <ellipse cx="30" cy="28" rx="20" ry="18" style={{ fill: 'var(--stone)' }}/>
      <ellipse cx="30" cy="28" rx="13" ry="13" style={{ fill: 'var(--surf)' }}/>
      <ellipse cx="22" cy="25" rx="5.5" ry="6.5" style={{ fill: 'var(--ink)' }}/>
      <ellipse cx="38" cy="25" rx="5.5" ry="6.5" style={{ fill: 'var(--ink)' }}/>
      <circle cx="24" cy="23" r="2" fill="white"/>
      <circle cx="40" cy="23" r="2" fill="white"/>
      <path d="M21 39 Q30 47 39 39" fill="none" style={{ stroke: 'var(--sage)' }} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  )
}

const MINI_CATS = [MiniCatSad, MiniCatAnxious, MiniCatCalm, MiniCatHappy, MiniCatExcited]

/* ── Full animated cat poses ── */
function CatSad() {
  return (
    <g id="sad-pose">
      <ellipse cx="80" cy="130" rx="52" ry="24" style={{ fill: 'var(--stone)', animation: 'breathe 4s ease-in-out infinite' }}/>
      <ellipse cx="80" cy="130" rx="34" ry="15" style={{ fill: 'var(--surf)' }}/>
      <g style={{ transformOrigin: '28px 130px', animation: 'tail-droop 5s ease-in-out infinite' }}>
        <path d="M28 130 Q8 138 6 150 Q4 162 16 164" fill="none" style={{ stroke: 'var(--sage)' }} strokeWidth="8" strokeLinecap="round"/>
      </g>
      <ellipse cx="106" cy="110" rx="16" ry="14" style={{ fill: 'var(--stone)' }}/>
      <polygon points="98,99 90,82 104,92" style={{ fill: 'var(--stone)' }}/>
      <polygon points="114,99 122,82 108,92" style={{ fill: 'var(--stone)' }}/>
      <ellipse cx="106" cy="110" rx="12" ry="11" style={{ fill: 'var(--surf)' }}/>
      <ellipse cx="100" cy="107" rx="3" ry="2" style={{ fill: 'var(--ink)' }}/>
      <ellipse cx="112" cy="107" rx="3" ry="2" style={{ fill: 'var(--ink)' }}/>
      <ellipse cx="106" cy="113" rx="2" ry="1.5" style={{ fill: 'var(--accent)' }}/>
      <path d="M101 117 Q106 114 111 117" fill="none" style={{ stroke: 'var(--sage)' }} strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="94" y1="109" x2="78" y2="106" style={{ stroke: 'var(--sage)' }} strokeWidth="0.8"/>
      <line x1="94" y1="112" x2="76" y2="112" style={{ stroke: 'var(--sage)' }} strokeWidth="0.8"/>
      <line x1="118" y1="109" x2="134" y2="106" style={{ stroke: 'var(--sage)' }} strokeWidth="0.8"/>
      <line x1="118" y1="112" x2="136" y2="112" style={{ stroke: 'var(--sage)' }} strokeWidth="0.8"/>
    </g>
  )
}

function CatAnxious() {
  return (
    <g id="anxious-pose" style={{ animation: 'tremble 0.4s ease-in-out infinite' }}>
      <ellipse cx="80" cy="134" rx="40" ry="20" style={{ fill: 'var(--stone)' }}/>
      <ellipse cx="80" cy="98" rx="26" ry="24" style={{ fill: 'var(--stone)' }}/>
      <polygon points="60,78 52,56 66,72" style={{ fill: 'var(--stone)' }}/>
      <polygon points="100,78 108,56 94,72" style={{ fill: 'var(--stone)' }}/>
      <ellipse cx="80" cy="98" rx="16" ry="16" style={{ fill: 'var(--surf)' }}/>
      <ellipse cx="72" cy="95" rx="6" ry="7" style={{ fill: 'var(--ink)' }}/>
      <ellipse cx="88" cy="95" rx="6" ry="7" style={{ fill: 'var(--ink)' }}/>
      <circle cx="73" cy="93" r="1.5" fill="white"/>
      <circle cx="89" cy="93" r="1.5" fill="white"/>
      <ellipse cx="80" cy="102" rx="2" ry="1.5" style={{ fill: 'var(--accent)' }}/>
      <path d="M74 107 Q77 105 80 107 Q83 105 86 107" fill="none" style={{ stroke: 'var(--sage)' }} strokeWidth="1.2" strokeLinecap="round"/>
      <g style={{ transformOrigin: '80px 134px', animation: 'tail-slow 1s ease-in-out infinite' }}>
        <path d="M80 134 Q56 140 50 132 Q44 122 56 116" fill="none" style={{ stroke: 'var(--sage)' }} strokeWidth="7" strokeLinecap="round"/>
      </g>
      <line x1="64" y1="94" x2="46" y2="90" style={{ stroke: 'var(--sage)' }} strokeWidth="1"/>
      <line x1="64" y1="98" x2="44" y2="98" style={{ stroke: 'var(--sage)' }} strokeWidth="1"/>
      <line x1="96" y1="94" x2="114" y2="90" style={{ stroke: 'var(--sage)' }} strokeWidth="1"/>
      <line x1="96" y1="98" x2="116" y2="98" style={{ stroke: 'var(--sage)' }} strokeWidth="1"/>
    </g>
  )
}

function CatCalm() {
  return (
    <g id="calm-pose">
      <g style={{ transformOrigin: '80px 152px', animation: 'tail-slow 5s ease-in-out infinite' }}>
        <path d="M80 152 Q54 158 46 148 Q38 136 52 130" fill="none" style={{ stroke: 'var(--sage)' }} strokeWidth="8" strokeLinecap="round"/>
      </g>
      <ellipse cx="80" cy="128" rx="36" ry="30" style={{ fill: 'var(--stone)', animation: 'breathe 4s ease-in-out infinite' }}/>
      <ellipse cx="80" cy="96" rx="28" ry="26" style={{ fill: 'var(--stone)' }}/>
      <polygon points="60,75 52,54 68,70" style={{ fill: 'var(--stone)' }}/>
      <polygon points="100,75 108,54 92,70" style={{ fill: 'var(--stone)' }}/>
      <ellipse cx="80" cy="96" rx="18" ry="18" style={{ fill: 'var(--surf)' }}/>
      <path d="M68 93 Q73 97 78 93" fill="none" style={{ stroke: 'var(--ink)' }} strokeWidth="2" strokeLinecap="round"/>
      <path d="M82 93 Q87 97 92 93" fill="none" style={{ stroke: 'var(--ink)' }} strokeWidth="2" strokeLinecap="round"/>
      <ellipse cx="80" cy="101" rx="2.5" ry="2" style={{ fill: 'var(--accent)' }}/>
      <path d="M76 104 Q80 107 84 104" fill="none" style={{ stroke: 'var(--sage)' }} strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="62" y1="95" x2="44" y2="91" style={{ stroke: 'var(--sage)' }} strokeWidth="1"/>
      <line x1="62" y1="99" x2="42" y2="99" style={{ stroke: 'var(--sage)' }} strokeWidth="1"/>
      <line x1="98" y1="95" x2="116" y2="91" style={{ stroke: 'var(--sage)' }} strokeWidth="1"/>
      <line x1="98" y1="99" x2="118" y2="99" style={{ stroke: 'var(--sage)' }} strokeWidth="1"/>
    </g>
  )
}

function CatHappy() {
  return (
    <g id="happy-pose">
      <g style={{ transformOrigin: '80px 150px', animation: 'tail-wag 0.7s ease-in-out infinite' }}>
        <path d="M80 150 Q50 153 40 140 Q30 124 48 116" fill="none" style={{ stroke: 'var(--sage)' }} strokeWidth="8" strokeLinecap="round"/>
      </g>
      <ellipse cx="80" cy="126" rx="36" ry="30" style={{ fill: 'var(--stone)', animation: 'float-sit 3s ease-in-out infinite' }}/>
      <ellipse cx="80" cy="92" rx="28" ry="26" style={{ fill: 'var(--stone)', animation: 'float-sit 3s ease-in-out infinite' }}/>
      <polygon points="58,70 48,46 68,64" style={{ fill: 'var(--stone)' }}/>
      <polygon points="102,70 112,46 92,64" style={{ fill: 'var(--stone)' }}/>
      <ellipse cx="80" cy="92" rx="18" ry="18" style={{ fill: 'var(--surf)' }}/>
      <ellipse cx="71" cy="89" rx="5" ry="6" style={{ fill: 'var(--ink)' }}/>
      <ellipse cx="89" cy="89" rx="5" ry="6" style={{ fill: 'var(--ink)' }}/>
      <circle cx="72.5" cy="87.5" r="1.8" fill="white"/>
      <circle cx="90.5" cy="87.5" r="1.8" fill="white"/>
      <ellipse cx="80" cy="97" rx="2.5" ry="2" style={{ fill: 'var(--accent)' }}/>
      <path d="M73 101 Q80 107 87 101" fill="none" style={{ stroke: 'var(--sage)' }} strokeWidth="1.6" strokeLinecap="round"/>
      <line x1="62" y1="90" x2="42" y2="85" style={{ stroke: 'var(--sage)' }} strokeWidth="1"/>
      <line x1="62" y1="94" x2="40" y2="94" style={{ stroke: 'var(--sage)' }} strokeWidth="1"/>
      <line x1="98" y1="90" x2="118" y2="85" style={{ stroke: 'var(--sage)' }} strokeWidth="1"/>
      <line x1="98" y1="94" x2="120" y2="94" style={{ stroke: 'var(--sage)' }} strokeWidth="1"/>
    </g>
  )
}

function CatExcited() {
  return (
    <g id="excited-pose">
      <g style={{ transformOrigin: '80px 148px', animation: 'tail-fast 0.38s ease-in-out infinite' }}>
        <path d="M80 148 Q48 148 38 132 Q28 114 46 106" fill="none" style={{ stroke: 'var(--sage)' }} strokeWidth="8" strokeLinecap="round"/>
      </g>
      <ellipse cx="80" cy="122" rx="36" ry="28" style={{ fill: 'var(--stone)', animation: 'bounce 0.5s ease-in-out infinite' }}/>
      <ellipse cx="80" cy="84" rx="30" ry="28" style={{ fill: 'var(--stone)', animation: 'bounce 0.5s ease-in-out infinite' }}/>
      <polygon points="54,62 42,36 66,56" style={{ fill: 'var(--stone)', animation: 'ear-perk 0.9s ease-in-out infinite' }}/>
      <polygon points="106,62 118,36 94,56" style={{ fill: 'var(--stone)', animation: 'ear-perk 0.9s ease-in-out 0.45s infinite' }}/>
      <ellipse cx="80" cy="84" rx="20" ry="19" style={{ fill: 'var(--surf)' }}/>
      <ellipse cx="70" cy="81" rx="6.5" ry="7.5" style={{ fill: 'var(--ink)' }}/>
      <ellipse cx="90" cy="81" rx="6.5" ry="7.5" style={{ fill: 'var(--ink)' }}/>
      <circle cx="72" cy="79" r="2.2" fill="white"/>
      <circle cx="92" cy="79" r="2.2" fill="white"/>
      <ellipse cx="80" cy="89" rx="3" ry="2" style={{ fill: 'var(--accent)' }}/>
      <path d="M72 94 Q80 101 88 94" fill="none" style={{ stroke: 'var(--sage)' }} strokeWidth="2" strokeLinecap="round"/>
      <line x1="60" y1="82" x2="38" y2="76" style={{ stroke: 'var(--sage)' }} strokeWidth="1.2"/>
      <line x1="60" y1="86" x2="36" y2="86" style={{ stroke: 'var(--sage)' }} strokeWidth="1.2"/>
      <line x1="60" y1="90" x2="38" y2="95" style={{ stroke: 'var(--sage)' }} strokeWidth="1.2"/>
      <line x1="100" y1="82" x2="122" y2="76" style={{ stroke: 'var(--sage)' }} strokeWidth="1.2"/>
      <line x1="100" y1="86" x2="124" y2="86" style={{ stroke: 'var(--sage)' }} strokeWidth="1.2"/>
      <line x1="100" y1="90" x2="122" y2="95" style={{ stroke: 'var(--sage)' }} strokeWidth="1.2"/>
    </g>
  )
}

const CAT_POSES = [CatSad, CatAnxious, CatCalm, CatHappy, CatExcited]

const CAT_PROMPTS = [
  'Sitting quietly beside you, just being here.',
  'Watching the world go by, no rush.',
  'Curled up, tail tucked, fully present.',
  'Blinking slowly at the window light.',
  'Listening, without needing to fix anything.',
  'Here with you, whatever the day holds.',
  'Noticing the small things — a warm patch of sun.',
]

export default function MoodTracker() {
  const [selected, setSelected] = useState(null)
  const [note, setNote]         = useState('')
  const [saved, setSaved]       = useState(false)
  const [editing, setEditing]   = useState(false)
  const [entries, setEntries]   = useState([])

  useEffect(() => { api.getMoods().then(rows => setEntries(rows || [])) }, [])

  const { theme } = useContext(ThemeContext)
  const { Emoji } = useEmoji()

  const todayStr   = todayShort()
  const todayEntry = entries.find(e => e.date === todayStr) ?? null
  const showForm   = !todayEntry || editing

  const state   = selected !== null ? STATES[selected] : null
  const CatPose = selected !== null ? CAT_POSES[selected]
    : (todayEntry ? CAT_POSES[todayEntry.level] : CAT_POSES[2])

  const catPrompt = CAT_PROMPTS[new Date().getDay()]

  function startEdit() {
    setSelected(todayEntry.level)
    setNote(todayEntry.note || '')
    setEditing(true)
  }

  function save() {
    if (selected === null) return
    const entry = { date: todayStr, level: selected, note: note.trim(), ts: Date.now() }
    const filtered = entries.filter(e => e.date !== todayStr)
    const next = [entry, ...filtered].slice(0, 90)
    setEntries(next)
    api.saveMood(entry)
    setNote('')
    setSelected(null)
    setEditing(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2500)
  }

  return (
    <Card label="Today's check-in" title="How are you feeling?">
      <div className="mood-card-inner">

        {/* Animated cat or theme mascot */}
        <div className="cat-wrap">
          {theme.mascotImage ? (
            <img
              src={theme.mascotImage}
              alt="mascot"
              style={{ width: theme.mascotSize || 160, objectFit: 'contain', display: 'block', margin: '0 auto' }}
            />
          ) : (
            <svg width="160" height="176" viewBox="0 0 160 170">
              <CatPose />
            </svg>
          )}
          <p className="cat-caption">
            {state?.caption || (todayEntry && !editing ? STATES[todayEntry.level].caption : catPrompt)}
          </p>
        </div>

        <div className="mood-controls">
          {showForm ? (
            <>
              {/* Mood buttons */}
              <div className="mood-scale">
                {STATES.map((s, i) => (
                  <button
                    key={s.pose}
                    className={`mood-btn ${selected === i ? 'active' : ''}`}
                    style={selected === i ? { borderColor: 'var(--accent)', background: 'var(--bg)' } : {}}
                    onClick={() => setSelected(i)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 40 }}>
                      <Emoji name={s.pose} size={40} />
                    </div>
                    <span className="btn-label">{s.name}</span>
                  </button>
                ))}
              </div>

              <p className="mood-desc">{state?.desc || ''}</p>

              <textarea
                className="note-area"
                rows={2}
                placeholder="Anything on your mind? A single word is enough..."
                value={note}
                onChange={e => setNote(e.target.value)}
              />

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  className={`save-btn ${selected !== null ? 'ready' : ''}`}
                  onClick={save}
                >
                  Save today's check-in
                </button>
                {editing && (
                  <button className="ghost-btn" style={{ fontSize: '0.8rem' }} onClick={() => { setEditing(false); setSelected(null); setNote('') }}>
                    Cancel
                  </button>
                )}
                {saved && (
                  <span style={{ fontSize: '0.82rem', color: 'var(--sage)', fontStyle: 'italic' }}>
                    Saved ✓
                  </span>
                )}
              </div>
            </>
          ) : (
            /* Saved state */
            <div className="mood-saved-wrap">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Emoji name={STATES[todayEntry.level].pose} size={36} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: '0.95rem', color: 'var(--ink)', fontWeight: 500 }}>
                    {STATES[todayEntry.level].name}
                  </p>
                  {todayEntry.note && (
                    <p style={{ fontSize: '0.83rem', color: 'var(--stone)', fontStyle: 'italic', marginTop: 2, wordBreak: 'break-word' }}>
                      {todayEntry.note}
                    </p>
                  )}
                </div>
                <button className="ghost-btn" style={{ fontSize: '0.8rem', flexShrink: 0 }} onClick={startEdit}>
                  Edit
                </button>
              </div>
            </div>
          )}

          {/* Recent log */}
          {entries.length > 0 && (
            <div>
              <p className="log-title">Recent days</p>
              {entries.slice(0, 3).map((e, i) => {
                const s = STATES[e.level]
                return (
                  <div key={i} className="log-entry">
                    <span className="log-icon" style={{ color: s.color }}>{s.icon}</span>
                    <span className="log-date">{e.date}</span>
                    <span className="log-mood">{s.name}</span>
                    {e.note && <span className="log-note">{e.note}</span>}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}
